import { cn, formatNumber, formatPercent } from '@/lib/utils';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import type { ReactNode } from 'react';

interface MetricCardProps {
    title: string;
    value: number | string;
    change?: number;       // percentage change
    changePeriod?: string; // e.g. "vs. semana pasada"
    icon?: ReactNode;
    iconColor?: string;
    format?: 'number' | 'currency' | 'none';
    className?: string;
}

export function MetricCard({
    title,
    value,
    change,
    changePeriod = 'vs. semana pasada',
    icon,
    iconColor = 'text-primary',
    format = 'number',
    className,
}: MetricCardProps) {
    const displayValue = format === 'number' && typeof value === 'number'
        ? formatNumber(value)
        : String(value);

    const isPositive = change !== undefined && change > 0;
    const isNegative = change !== undefined && change < 0;
    const isNeutral = change === undefined || change === 0;

    return (
        <div
            className={cn(
                'rounded-xl border border-border bg-card p-4 sm:p-5 min-w-0 overflow-hidden',
                'hover:border-border/80 hover:shadow-lg hover:shadow-black/5',
                'transition-all duration-200 group',
                className
            )}
        >
            <div className="flex items-start justify-between gap-2 mb-3">
                <span className="text-sm font-medium text-muted-foreground truncate min-w-0">{title}</span>
                {icon && (
                    <div className={cn(
                        'p-2 rounded-lg bg-muted/50 group-hover:scale-110 transition-transform shrink-0',
                        iconColor
                    )}>
                        {icon}
                    </div>
                )}
            </div>

            <div className="flex flex-col gap-1.5 min-w-0">
                <span className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight leading-none break-words">
                    {displayValue}
                </span>

                {change !== undefined && (
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 min-w-0">
                        <div className={cn(
                            'inline-flex items-center gap-0.5 text-xs font-semibold rounded-full px-1.5 py-0.5 shrink-0',
                            isPositive && 'text-emerald-400 bg-emerald-500/10',
                            isNegative && 'text-red-400 bg-red-500/10',
                            isNeutral && 'text-muted-foreground bg-muted'
                        )}>
                            {isPositive && <TrendingUp size={12} />}
                            {isNegative && <TrendingDown size={12} />}
                            {isNeutral && <Minus size={12} />}
                            <span>{formatPercent(change)}</span>
                        </div>
                        <span className="text-[10px] text-muted-foreground truncate">{changePeriod}</span>
                    </div>
                )}
            </div>
        </div>
    );
}
